import React, { useEffect, useState, useRef } from "react";
import IMG from "../../Images/logo-sm.png";
import { useNavigate } from "react-router-dom";
import { getMenuAll } from "../../Redux/SideBarSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { handleLogout } from "../../Redux/AuthSlice";
import { ImageUrl } from "../Common/API";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { LuLayoutDashboard, LuUser, LuSettings, LuLogOut } from "react-icons/lu";

const Sidebar = ({ isVisible, setSidebarOpen, sidebarOpen, setIsVisible }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const path = window.location.pathname;
  const store = useSelector((state) => state.root.Sidebar);
  const { userDetails } = useSelector((state) => state.root.auth);
  const [MenuData, setMenuData] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    dispatch(getMenuAll({}));
  }, [dispatch]);

  useEffect(() => {
    if (store?.data?.menu) {
      const currentPath = window.location.pathname;
      const formattedMenuData = store?.data?.menu
        .map((item) => ({
          title: item.pageName,
          path: item.path,
          isView: item.isView,
          iconSrc: item.icon,
          iconAlt: item.alt,
          sortBy: item.sortBy || 0,
          isActive: item.path === currentPath,
          submenu: item?.submenu,
          isBottomMenu: item.bottummenu === true || item.bottommenu === true || item.bottomMenu === true || item.bottummenu === 1 || item.bottommenu === 1 || item.bottomMenu === 1 || item.bottumMenu === 1 || item.bottumMenu === true,
        }))
        .sort((a, b) => a?.sortBy - b?.sortBy || a?.title?.localeCompare(b?.title));
      setMenuData(formattedMenuData);

      // Auto-expand menu group containing active path
      const activeGroup = formattedMenuData.find((m) =>
        m.submenu?.some((s) => s.path === currentPath)
      );
      if (activeGroup) {
        setExpandedItem(activeGroup.sortBy);
        setSidebarOpen(activeGroup.sortBy);
      }
    }
  }, [store?.data?.menu, path]);

  const handleMenuClick = (item) => {
    if (item?.title === "Log Out") {
      toast.loading("Logout...");
      setTimeout(() => {
        dispatch(handleLogout());
        toast.remove();
        navigate("/");
      }, 1000);
      return;
    }
    const hasSubmenu = item?.submenu?.filter((s) => s.isView)?.length > 0;
    if (hasSubmenu) {
      setExpandedItem((prev) => (prev === item.sortBy ? null : item.sortBy));
      setSidebarOpen(item.sortBy);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const initials = userDetails?.userName
    ? userDetails.userName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
    : "D";

  const visibleMenu = MenuData.filter(
    (item) =>
      item?.isView &&
      (item?.submenu?.some((x) => x?.isView) ||
        item?.path ||
        item?.title === "Log Out")
  );

  const topMenu = visibleMenu.filter((item) => !item.isBottomMenu);
  const bottomMenu = store?.data?.bottummenu?.filter(item => item.isView) || [];

  return (
    <div
      className={`fixed top-0 left-0 h-screen bg-white border-r border-slate-100 flex flex-col z-[99] transition-all duration-300 w-64 ${isVisible ? "translate-x-0" : "-translate-x-full"
        }`}
    >
      {/* Logo Header */}
      <div className="h-16 flex items-center px-6 border-b border-slate-50 gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg  flex items-center justify-center text-white shadow-md shadow-indigo-100">
          <img src={IMG} alt="Disploy" className="h-8 w-8 object-contain" />
        </div>
        <span className="font-semibold text-slate-800 text-lg tracking-wider font-sans">CRM</span>
      </div>

      {/* Nav Menu */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1.5 custom-sidebar-scrollbar">
        {topMenu.map((item) => {
          const isExpanded = expandedItem === item.sortBy;
          const visibleSubs = item?.submenu?.filter((s) => s.isView) || [];
          const isParentActive = visibleSubs.some((s) => s.path === path) || path === item.path;

          return (
            <div key={item.sortBy} className="space-y-1">
              <button
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${isParentActive
                  ? "bg-indigo-50/50 text-indigo-600 font-semibold"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                onClick={() => handleMenuClick(item)}
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    {item.iconSrc && (
                      <img
                        src={item.iconSrc}
                        alt={item.iconAlt || item.title}
                        className={`w-4.5 h-4.5 transition-all duration-150 ${isParentActive
                          ? "filter-indigo-icon"
                          : "filter-slate-icon"
                          }`}
                      />
                    )}
                  </span>
                  <span>{item.title}</span>
                </div>
                {visibleSubs.length > 0 && (
                  <span className="text-slate-400 group-hover:text-slate-600 transition-colors">
                    {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                  </span>
                )}
              </button>

              {/* Accordion Sub-menu */}
              {isExpanded && visibleSubs.length > 0 && (
                <div className="pl-6 space-y-1 border-l border-slate-100 ml-5.5 my-1">
                  {visibleSubs.map((sub, si) => {
                    const isSubActive = path === sub.path;
                    return (
                      <a
                        key={si}
                        onClick={() => navigate(sub.path)}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all duration-150 ${isSubActive
                          ? "bg-indigo-50/30 text-indigo-600 font-semibold"
                          : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
                          }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${isSubActive ? "bg-indigo-500 scale-120" : "bg-slate-300"
                              }`}
                          ></span>
                          <span>{sub.pageName}</span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {/* Bottom Menu */}
        {bottomMenu.length > 0 && (
          <div className="py-3 border-t border-slate-100 space-y-1.5 flex-shrink-0 bg-white">
            {bottomMenu.map((item) => {
              const isExpanded = expandedItem === item.sortBy;
              const visibleSubs = item?.submenu?.filter((s) => s.isView) || [];
              const isParentActive = path === item.path;

              return (
                <div key={item.sortBy} className="space-y-1">
                  <button
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${isParentActive
                      ? "bg-indigo-50/50 text-indigo-600 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    onClick={() => handleMenuClick(item)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 flex items-center justify-center">
                        {item.icon && (
                          <img
                            src={item.icon}
                            alt={item.pageName}
                            className={`w-4.5 h-4.5 transition-all duration-150 ${isParentActive
                              ? "filter-indigo-icon"
                              : "filter-slate-icon"
                              }`}
                          />
                        )}
                      </span>
                      <span>{item.pageName}</span>
                    </div>
                    {/* {visibleSubs.length > 0 && (
                    <span className="text-slate-400 group-hover:text-slate-600 transition-colors">
                      {isExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                    </span>
                  )} */}
                  </button>

                  {/* Accordion Sub-menu */}
                  {isExpanded && visibleSubs.length > 0 && (
                    <div className="pl-6 space-y-1 border-l border-slate-100 ml-5.5 my-1">
                      {visibleSubs.map((sub, si) => {
                        const isSubActive = path === sub.path;
                        return (
                          <a
                            key={si}
                            onClick={() => navigate(sub.path)}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150 ${isSubActive
                              ? "bg-indigo-50/30 text-indigo-600 font-semibold"
                              : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
                              }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span
                                className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${isSubActive ? "bg-indigo-500 scale-120" : "bg-slate-300"
                                  }`}
                              ></span>
                              <span>{sub.pageName}</span>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>



      {/* Upgrade Plan Box */}
      {/* <div className="px-4 py-4 border-t border-slate-50 bg-slate-50/30 flex-shrink-0">
        <div className="p-4 rounded-xl bg-indigo-50/60 border border-indigo-100/50 flex flex-col items-center text-center gap-2.5 relative overflow-hidden">
          <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-indigo-100/30 rounded-full blur-sm"></div>
          <div className="w-9 h-9 rounded-lg bg-indigo-600/10 flex items-center justify-center text-indigo-600 mb-1">
            <HiOutlineLightningBolt size={18} />
          </div>
          <p className="font-semibold text-slate-800 text-xs">Upgrade Plan</p>
          <p className="text-[10px] text-slate-500 max-w-[160px] leading-relaxed">
            Upgrade to unlock more premium features.
          </p>
          <button className="w-full mt-1.5 py-1.5 px-3 rounded-lg border border-indigo-600 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all duration-150">
            Upgrade Now
          </button>
        </div>
      </div> */}

      {/* User Profile Footer */}
      <div className="relative" ref={profileRef}>
        {profileDropdownOpen && (
          <div className="absolute bottom-16 left-4 right-4 bg-white border border-slate-100 rounded-xl shadow-xl z-50 overflow-hidden py-1.5 space-y-0.5 animate-in fade-in slide-in-from-bottom-2 duration-150">
            <div className="px-3.5 py-2 border-b border-slate-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {userDetails?.profilePic ? (
                  <img src={`${userDetails?.profilePic}`} alt="user" className="w-full h-full object-cover" />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <div className="min-w-0 text-left">
                <p className="text-xs font-bold text-slate-800 truncate">{userDetails?.userName || "Dhruv Admin"}</p>
                <p className="text-[10px] text-slate-400 truncate mt-0.5">{userDetails?.email || "admin@disploy.com"}</p>
              </div>
            </div>
            {[
              { icon: <LuLayoutDashboard size={13} />, label: "Dashboard", action: () => navigate("/Analytics") },
              { icon: <LuUser size={13} />, label: "My Profile", action: () => { } },
              { icon: <LuSettings size={13} />, label: "Settings", action: () => { } },
            ].map((m, i) => (
              <button
                key={i}
                onClick={() => { m.action(); setProfileDropdownOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-slate-600 hover:text-indigo-600 hover:bg-slate-50 transition-colors text-left font-medium"
              >
                {m.icon}
                <span>{m.label}</span>
              </button>
            ))}
            <div className="h-px bg-slate-50 my-1"></div>
            <button
              onClick={() => {
                setProfileDropdownOpen(false);
                toast.loading("Logout...");
                setTimeout(() => {
                  dispatch(handleLogout());
                  toast.remove();
                  navigate("/");
                }, 1000);
              }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors text-left font-medium"
            >
              <LuLogOut size={13} />
              <span>Log Out</span>
            </button>
          </div>
        )}

        {/* Profile Trigger Button */}
        {/* <button
          onClick={() => setProfileDropdownOpen((prev) => !prev)}
          className="w-full flex items-center justify-between p-4 border-t border-slate-100 hover:bg-slate-50 transition-colors flex-shrink-0"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
              {userDetails?.profilePic ? (
                <img src={`${userDetails?.profilePic}`} alt="user" className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-xs font-bold text-slate-800 truncate">{userDetails?.userName || "Dhruv Admin"}</p>
              <p className="text-[10px] text-slate-400 truncate mt-0.5">{userDetails?.email || "admin@disploy.com"}</p>
            </div>
          </div>
          <span className="text-slate-400">
            {profileDropdownOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          </span>
        </button> */}
      </div>
    </div>
  );
};

export default Sidebar;
